import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetCategories, adminCreateCategory } from '../../../services/admin'

export function Categories() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('categories')
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setCategories(await adminGetCategories())
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleCreate = async (e) => {
    e.preventDefault()
    setCreateError(null)
    if (!newName.trim()) { setCreateError('Category name is required'); return }
    setCreating(true)
    try {
      await adminCreateCategory(newName.trim())
      setNewName('')
      setSuccessMsg(`Category "${newName.trim()}" created`)
      setTimeout(() => setSuccessMsg(null), 4000)
      load()
    } catch (err) {
      setCreateError(err.message ?? 'Failed to create category')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {successMsg && <AdminAlert type="success" message={successMsg} />}
      {error && <AdminAlert type="error" message={error.message ?? 'Failed to load categories'} />}

      {/* Create */}
      {canEdit && <div
        className="border-4 border-on-background bg-surface p-5 flex flex-col gap-4"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}
      >
        <h3 className="font-extrabold text-[13px] uppercase tracking-wider">Create Category</h3>
        {createError && <AdminAlert type="error" message={createError} />}
        <form onSubmit={handleCreate} className="flex gap-3 flex-wrap">
          <input
            type="text"
            placeholder="e.g. Technology"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            disabled={creating}
            className="flex-1 min-w-[180px] bg-surface border-2 border-on-background px-3 py-2.5 text-[13px] focus:outline-none focus:border-primary-container"
          />
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            className="px-5 py-2.5 border-4 border-on-background bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}
          >
            {creating ? 'Creating…' : '+ Create'}
          </button>
        </form>
      </div>}

      {/* List */}
      <div className="flex flex-col gap-3">
        <h3 className="font-extrabold text-[13px] uppercase tracking-wider">
          All Categories{!loading && ` (${categories.length})`}
        </h3>

        {loading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded" />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">
              No categories yet{canEdit ? ' — create one above' : ''}
            </p>
          </div>
        ) : (
          <div
            className="border-4 border-on-background bg-surface overflow-x-auto"
            style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}
          >
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-4 border-on-background bg-surface-container-low">
                  <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest">Name</th>
                  <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest">ID</th>
                  <th className="px-4 py-3 font-bold text-[10px] uppercase tracking-widest">Created</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr
                    key={cat.id}
                    className="border-b-2 border-on-background/15 hover:bg-surface-container-low/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-bold text-[13px]">{cat.name}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-on-surface-variant">{cat.id}</td>
                    <td className="px-4 py-3 text-[12px] text-on-surface-variant">
                      {cat.createdAt ? new Date(cat.createdAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}